import { GoogleLogin } from '@react-oauth/google';
import { Themed } from '@theme-ui/mdx';
import { Link, navigate } from 'gatsby';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactModal from 'react-modal';
import { Loader } from 'semantic-ui-react';
import { Box, Button, Checkbox, Field, Flex, Grid, Label } from 'theme-ui';
import { useUserUpdate } from '../services/userContext';
import { AdvervioLogin } from './AdservioLogin';

import ModalLayout, {
  closeTimeoutMS,
  defaultModalStyles,
} from '@src/components/layout/modalLayout';
import pageSlugs from '@src/components/layout/pageSlugs';
import { setGoogleToken } from '@src/services/auth';
import api from '@src/utils/api.js';
const EventBYRole = {
  2: 'Teacher',
  3: 'Student',
  4: 'Student representative',
};
const loginModal = ({ isOpen, onClose, onReset }) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const [errorMessage, setErrorMessage] = useState('');
  const [googleErrorMessage, setGoogleErrorMessage] = useState('');
  const updateUser = useUserUpdate();

  const handleClose = () => {
    setErrorMessage();
    setGoogleErrorMessage();
    reset();
    onClose();
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    setErrorMessage('');

    const dto = {
      email: data.email,
      password: data.password,
      rememberMe: data.rememberMe,
    };

    try {
      const response = await api.post('auth/login', dto);
      if (response?.authToken) {
        updateUser({
          id: response?.id,
          userToken: response?.authToken,
          email_confirmed: response?.email_confirmed,
          email: response?.email,
          isInProject: response?.isInProject,
          complete: response?.complete,
          avatar_url: response?.avatar_url,
          role: response?.role,
          display_name: response?.display_name,
          childrenData: response?.childrenData,
        });

        window.dataLayer.push({
          event: 'login',
          method: 'Direct',
          user_type: EventBYRole[response?.role],
        });
        handleClose();
      }

      let errorMessage = '';

      if (response?.errorCode) {
        errorMessage = t('error.unknownError');

        if (response?.errorCode === 'ACCOUNT_NOT_VERIFIED') {
          errorMessage = t('error.accountNotVerified');
        }

        if (response?.errorCode === 'FAILED_TO_LOGIN') {
          errorMessage = t('error.failedToLogin');
        }
        if (response?.errorCode === 'ACCOUNT_BLOCKED') {
          errorMessage = t('error.userBlocked');
        }

        setErrorMessage(errorMessage);
      }
    } catch (error) {
      let errorMessage = '';

      if (error?.response?.data?.errorCode) {
        errorMessage = t('error.unknownError');
      }

      if (error?.response?.data?.errorCode === 'ACCOUNT_NOT_VERIFIED') {
        errorMessage = t('error.accountNotVerified');
      }

      if (error?.response?.data?.errorCode === 'FAILED_TO_LOGIN') {
        errorMessage = t('error.failedToLogin');
      }

      setErrorMessage(error);
    }

    setIsLoading(false);
  };

  const googleFailure = (response) => {
    setGoogleErrorMessage(t('GoogleLoginError'));
  };

  const googleSuccess = async (googleResponse) => {
    const dto = {
      googleToken: googleResponse.credential,
    };

    try {
      const response = await api.post('auth/googleLogin', dto);

      if (response?.errorCode === 'GOOGLE_LOGIN_FAILED_USER_NOT_FOUND') {
        setGoogleToken(dto);
        handleClose();
        navigate(`/${pageSlugs.registracija[i18n.language]}`);
      }

      if (response?.authToken) {
        updateUser({
          id: response?.id,
          isInProject: response?.isInProject,
          userToken: response?.authToken,
          email_confirmed: response?.email_confirmed,
          email: response?.email,
          complete: response?.complete,
          avatar_url: response?.avatar_url,
          role: response?.role,
          display_name: response?.display_name,
          childrenData: response?.childrenData,
        });
        window.dataLayer.push({
          event: 'login',
          method: 'Google',
          user_type: EventBYRole[response?.role],
        });
        handleClose();
      }
    } catch (error) {
      googleFailure();
    }
  };

  return (
    <ReactModal
      isOpen={isOpen}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      ariaHideApp={false}
      onRequestClose={handleClose}
      closeTimeoutMS={closeTimeoutMS}
      style={defaultModalStyles}
    >
      <ModalLayout>
        <Box
          bg="white"
          sx={{
            position: 'relative',
            borderRadius: '10px',
            overflow: 'hidden',
            height: '100%',
          }}
        >
          <Button
            variant="empty"
            sx={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              fontSize: '14px',
              color: 'text',
            }}
            onClick={handleClose}
          >
            {t('close')}
          </Button>
          <Grid columns={[1, 1, 2]} gap={0}>
            <Box px={['30px', '60px']} py={4}>
              <Themed.h3 sx={{ textAlign: 'center', fontSize: '28px' }}>
                {t('logInForUsers')}
              </Themed.h3>
              <form
                onSubmit={handleSubmit(onSubmit)}
                sx={{ maxWidth: '350px', margin: 'auto' }}
              >
                <fieldset disabled={isLoading}>
                  <Box
                    className="labels-on-inputs"
                    sx={{ paddingBottom: '7px' }}
                  >
                    <span sx={{ color: 'error' }}>{errorMessage}</span>
                    <Field
                      label={t('email')}
                      name="email"
                      type="email"
                      {...register('email', {
                        required: true,
                      })}
                      className={errors.email ? 'field-error' : ''}
                    />
                    {errors.email && (
                      <span sx={{ color: 'error' }}>{t('fieldRequired')}</span>
                    )}
                  </Box>
                  <Box className="labels-on-inputs">
                    <Field
                      label={t('password')}
                      name="password"
                      type="password"
                      className={errors.password ? 'field-error' : ''}
                      {...register('password', {
                        required: true,
                      })}
                    />
                    {errors.password && (
                      <span sx={{ color: 'error' }}>{t('fieldRequired')}</span>
                    )}
                  </Box>

                  <Flex py={3}>
                    <Label sx={{ width: 'auto' }} mb={3}>
                      <Checkbox {...register('rememberMe')} />
                      {t('rememberMe')}
                    </Label>
                    <Themed.a
                      sx={{
                        marginLeft: 'auto',
                        color: 'primary',
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        if (onReset) {
                          onReset();
                        } else {
                          navigate(
                            `/${pageSlugs.passwordReset[i18n.language]}`
                          );
                        }
                      }}
                    >
                      {t('forgotPassword')}
                    </Themed.a>
                  </Flex>
                  <Button className="w-100" type="submit">
                    {isLoading && (
                      <Loader inline active size="small" inverted="center" />
                    )}
                    {!isLoading && t('logIn')}
                  </Button>

                  <Flex py={3} sx={{ alignItems: 'center' }}>
                    <p>{t('doNotHaveAccount')}</p>
                    <Link
                      sx={{
                        marginLeft: 'auto',
                        color: 'primary',
                      }}
                      to={`/${pageSlugs.registracija[i18n.language]}`}
                    >
                      {t('register')}
                    </Link>
                  </Flex>
                </fieldset>
              </form>
            </Box>
            <Box
              bg="primaryPaleBg"
              px={['30px', '60px']}
              py={4}
              sx={{ fontSize: '20px' }}
            >
              <Themed.h3 sx={{ textAlign: 'center', fontSize: '28px' }}>
                {t('otherLogins')}
              </Themed.h3>
              <Box
                sx={{
                  paddingTop: '25px',
                  paddingBottom: '32px',
                }}
              >
                <GoogleLogin
                  locale={i18n.language}
                  size="large"
                  theme="filled_blue"
                  width="350px"
                  onSuccess={googleSuccess}
                  onFailure={googleFailure}
                  cookiePolicy={'single_host_origin'}
                />
                {googleErrorMessage && (
                  <span sx={{ color: 'error' }}>{googleErrorMessage}</span>
                )}
              </Box>
              {`${process.env.GATSBY_ADSERVIO_ON}` === 'TRUE' ? (
                <Box sx={{ paddingBottom: '32px' }}>
                  <AdvervioLogin width="350px" />
                </Box>
              ) : (
                <></>
              )}

              {/* <Box>
                <Button
                  variant="inverted"
                  sx={{ backgroundColor: 'white' }}
                  className="w-100"
                >
                  {t('loginWithFacebook')}
                </Button>
              </Box> */}
            </Box>
          </Grid>
        </Box>
      </ModalLayout>
    </ReactModal>
  );
};

export default loginModal;
